import { css } from "lit";
import { ImperativeElement } from "../view.ts";
import { type PropertyValues } from "lit";
import { customElement, property } from "lit/decorators.js";
import * as Styles from "../../styles.ts";
import { select } from "d3-selection";
import { scaleLinear } from "d3-scale";
import { axisBottom, axisLeft } from "d3-axis";
import { type D3ZoomEvent, zoom as zoomBehavior, zoomIdentity } from "d3-zoom";
import {
  calcInnerSize,
  calcSizeAsAspectRatioOfHeight,
  type Margin,
} from "../../util/coords.ts";

const styles = css`
  :host {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100cqh;
    width: 100cqw;
    container-type: size;
  }

  #scatterplot {
    height: 100%;
    width: 100%;
  }

  .viewport-border {
    fill: none;
    stroke: var(--border);
    stroke-width: 1;
  }

  .axis {
    stroke: var(--muted-foreground);
    stroke-width: 1;
  }

  .axis-label {
    font-size: var(--font-size-sm);
    fill: var(--foreground);
  }

  .divider {
    stroke: var(--border);
    stroke-width: 1;
  }

  .link {
    stroke: var(--muted-foreground);
    stroke-width: 1;
    fill: none;
    transition: opacity var(--duration-normal) ease-in-out;
  }

  .point {
    fill: var(--info);
    cursor: pointer;
    transition: opacity var(--duration-normal) ease-in-out;
    r: 5;
  }

  .point:hover {
    r: 8;
  }

  .label {
    pointer-events: none;
    transition: opacity var(--duration-normal) ease-in-out;
    overflow: visible;
    width: 1px;
    height: 1px;
  }

  .label div {
    font-size: var(--font-size-sm);
    color: var(--foreground);
    line-height: 1.3;
    word-wrap: break-word;
    overflow-wrap: break-word;
    transform: translateY(-50%);
    width: 12em;
  }
`;

export type Model = {
  datapoints: Datapoint[];
  xAxisLabel: string;
  yAxisLabel: string;
  naturalHeight: number;
  aspectRatio: number;
};

export const model = ({
  datapoints = [],
  xAxisLabel = "",
  yAxisLabel = "",
  naturalHeight = 600,
  aspectRatio = 3 / 2,
}: Partial<Model> = {}) => ({
  datapoints,
  xAxisLabel,
  yAxisLabel,
  naturalHeight,
  aspectRatio,
});

export type Datapoint = {
  id: string;
  title: string;
  color?: string;
  x: number;
  y: number;
  links?: string[];
};

export class ActivateScatterplotDatapointEvent extends Event {
  detail: Datapoint;

  constructor(datapoint: Datapoint) {
    super("activate-scatterplot-datapoint", {
      bubbles: true,
      composed: true,
    });
    this.detail = datapoint;
  }
}

@customElement("scatter-plot")
export class Scatterplot extends ImperativeElement {
  static override styles = [Styles.base, styles];

  get #margin(): Margin {
    // Hard-coded based on label size
    return { top: 20, right: 12, bottom: 40, left: 48 };
  }

  #currentTransform = zoomIdentity;

  @property({ type: Object })
  accessor state: Model = model();

  #render(): void {
    const { datapoints, xAxisLabel, yAxisLabel, naturalHeight, aspectRatio } =
      this.state;

    const $root = select(this.shadowRoot as unknown as Document);
    const size = calcSizeAsAspectRatioOfHeight(naturalHeight, aspectRatio);
    const margin = this.#margin;
    const innerSize = calcInnerSize(size, margin);

    const $svg = $root.select("svg#scatterplot");
    const $g = $svg.select("g#scatterplot-group");
    const $clipped = $g.select("g#clipped-content");

    // Update clip path dimensions
    $svg
      .select("clipPath#plot-clip rect")
      .attr("width", innerSize.width)
      .attr("height", innerSize.height);

    // Update viewport border
    $g.select(".viewport-border")
      .attr("width", innerSize.width)
      .attr("height", innerSize.height);

    // Base scales (0 to 1)
    const xScaleBase = scaleLinear().domain([0, 1]).range([0, innerSize.width]);

    const yScaleBase = scaleLinear()
      .domain([0, 1])
      .range([innerSize.height, 0]);

    // Apply zoom transform to scales
    const xScale = this.#currentTransform.rescaleX(xScaleBase);
    const yScale = this.#currentTransform.rescaleY(yScaleBase);

    // Create axes
    const xAxis = axisBottom(xScale).ticks(3);
    const yAxis = axisLeft(yScale).ticks(3);

    // Redraw x-axis
    const $xAxis = $g
      .select<SVGGElement>("g#x-axis")
      .attr("transform", `translate(0,${innerSize.height})`);
    $xAxis.selectAll("*").remove();
    $xAxis.call(xAxis);

    const $yAxis = $g.select<SVGGElement>("g#y-axis");
    $yAxis.selectAll("*").remove();
    $yAxis.call(yAxis);

    // Update x label
    $g.select("#x-label")
      .attr(
        "transform",
        `translate(${innerSize.width / 2}, ${innerSize.height + 35})`,
      )
      .text(xAxisLabel);

    // Update y label
    $g.select("#y-label")
      .attr("y", 0 - margin.left)
      .attr("x", 0 - innerSize.height / 2)
      .text(yAxisLabel);

    // Update x divider
    $g.select("#x-divider")
      .attr("x1", xScale(0.5))
      .attr("x2", xScale(0.5))
      .attr("y1", 0)
      .attr("y2", innerSize.height);

    // Update y divider
    $g.select("#y-divider")
      .attr("x1", 0)
      .attr("x2", innerSize.width)
      .attr("y1", yScale(0.5))
      .attr("y2", yScale(0.5));

    // Draw links between datapoints
    const linkData: Array<{ source: Datapoint; target: Datapoint }> = [];
    for (const datapoint of datapoints) {
      if (datapoint.links) {
        for (const targetId of datapoint.links) {
          const target = datapoints.find((d) => d.id === targetId);
          if (target) {
            linkData.push({ source: datapoint, target });
          }
        }
      }
    }

    type LinkDatum = { source: Datapoint; target: Datapoint };
    $clipped
      .selectAll(".link")
      .data(linkData)
      .join("line")
      .attr("class", "link")
      .attr("x1", (d: LinkDatum) => xScale(d.source.x))
      .attr("y1", (d: LinkDatum) => yScale(d.source.y))
      .attr("x2", (d: LinkDatum) => xScale(d.target.x))
      .attr("y2", (d: LinkDatum) => yScale(d.target.y));

    // Add data points
    $clipped
      .selectAll(".point")
      .data(datapoints)
      .join("circle")
      .attr("class", "point")
      .attr("style", (d: Datapoint) => `fill: ${d.color ?? "#fff"};`)
      .attr("r", 5)
      .attr("cx", (d: Datapoint) => xScale(d.x))
      .attr("cy", (d: Datapoint) => yScale(d.y))
      .style(
        "filter",
        (d: Datapoint) => `drop-shadow(0 0 8px ${d.color ?? "#fff"})`,
      )
      .on("click", (_event: MouseEvent, d: Datapoint) => {
        this.dispatchEvent(new ActivateScatterplotDatapointEvent(d));
      });

    // Add wrapping text labels for each data point
    $clipped
      .selectAll<SVGForeignObjectElement, Datapoint>(".label")
      .data(datapoints)
      .join((enter) =>
        enter
          .append("foreignObject")
          .attr("class", "label")
          .call((fo) => fo.append("xhtml:div")),
      )
      .attr("x", (d: Datapoint) => xScale(d.x) + 16)
      .attr("y", (d: Datapoint) => yScale(d.y))
      .style("opacity", 0)
      .select("div")
      .text((d: Datapoint) => d.title);

    // Add hover interactions
    $clipped
      .selectAll(".point")
      .on("mouseenter", (_event: MouseEvent, d: unknown) => {
        const data = d as Datapoint;
        const linkedIds = new Set(data.links ?? []);
        linkedIds.add(data.id);

        // Find points that link TO the hovered point
        for (const point of datapoints) {
          if (point.links?.includes(data.id)) {
            linkedIds.add(point.id);
          }
        }

        // Dim all points except hovered and linked
        $clipped.selectAll(".point").style("opacity", (p: unknown) => {
          const point = p as Datapoint;
          return linkedIds.has(point.id) ? 1 : 0.2;
        });

        // Show labels for hovered node and linked nodes
        $clipped.selectAll(".label").style("opacity", (labelData: unknown) => {
          const point = labelData as Datapoint;
          return linkedIds.has(point.id) ? 1 : 0;
        });

        // Highlight links connected to hovered node
        $clipped.selectAll(".link").style("opacity", (linkData: unknown) => {
          const link = linkData as { source: Datapoint; target: Datapoint };
          return link.source.id === data.id || link.target.id === data.id
            ? 1
            : 0.1;
        });
      })
      .on("mouseleave", (_event: MouseEvent, _d: unknown) => {
        // Reset all styles
        $clipped.selectAll(".point").style("opacity", 1);
        $clipped.selectAll(".label").style("opacity", 0);
        $clipped.selectAll(".link").style("opacity", 1);
      });
  }

  override update(changedProperties: PropertyValues<this>): void {
    super.update(changedProperties);

    const { naturalHeight, aspectRatio } = this.state;
    const $root = select(this.shadowRoot as unknown as Document);

    if (this.isFirstUpdate) {
      // Create root SVG el and group
      const $svg = $root.append("svg").attr("id", "scatterplot");

      const $g = $svg.append("g").attr("id", "scatterplot-group");

      // Add clip path for plotting area
      $svg
        .append("defs")
        .append("clipPath")
        .attr("id", "plot-clip")
        .append("rect");

      // Add zoom behavior
      const zoom = zoomBehavior()
        .scaleExtent([1, 10])
        .on("zoom", (event: D3ZoomEvent<SVGSVGElement, unknown>) => {
          const transform = event.transform;

          this.#currentTransform = transform;
          this.#render();
        });

      // deno-lint-ignore no-explicit-any
      $svg.call(zoom as any);

      // Add X axis
      $g.append("g").attr("id", "x-axis").attr("class", "axis");

      // Add Y axis
      $g.append("g").attr("id", "y-axis").attr("class", "axis");

      // Add axis labels
      // X-axis label
      $g.append("text")
        .attr("id", "x-label")
        .attr("class", "axis-label")
        .style("text-anchor", "middle");

      // Y-axis label
      $g.append("text")
        .attr("id", "y-label")
        .attr("class", "axis-label")
        .attr("transform", "rotate(-90)")
        .attr("dy", "1em")
        .style("text-anchor", "middle");

      // Add dividing lines at 0.5
      // Vertical line at x = 0.5
      $g.append("line").attr("id", "x-divider").attr("class", "divider");

      // Horizontal line at y = 0.5
      $g.append("line").attr("id", "y-divider").attr("class", "divider");

      // Viewport border
      $g.append("rect").attr("class", "viewport-border");

      // Group for clipped content (data points, links, labels)
      $g.append("g")
        .attr("id", "clipped-content")
        .attr("clip-path", "url(#plot-clip)");
    }

    const size = calcSizeAsAspectRatioOfHeight(naturalHeight, aspectRatio);
    const margin = this.#margin;

    // Select SVG and set viewbox
    const $svg = $root
      .select("svg#scatterplot")
      .attr("viewBox", `0 0 ${size.width} ${size.height}`);

    // Set margins on main group
    $svg
      .select("g#scatterplot-group")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    this.#render();
  }
}
